/**
 * Inline click-to-edit title for a two-field (first/last name) CRM contact
 * entity in the record header.
 *
 * Renders the composed `ahoy_first_name`/`ahoy_last_name` title as a
 * click-to-edit pair of fields when the viewer holds rename permission on
 * BOTH properties; fails closed to a plain read-only title otherwise. Unlike
 * `RecordTitleSingleNameEditor` (single `ahoy_name` field, delegated to
 * `useInlineNameRename`), this editor drives `useInlineEdit` directly with a
 * composite `{ first, last }` draft so both fields persist in ONE wire event
 * — the events API has no way to submit two properties as two atomic events,
 * and `ahoy_display_name` must be derived from both parts together, not
 * recomputed from a half-updated pair. The dispatcher owns the previous-name
 * strikethrough; this component renders only the current title line.
 */

import {
  type FocusEvent,
  type KeyboardEvent,
  useCallback,
  useMemo,
  useRef,
} from 'react';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { useOrg } from '@/contexts/OrgContext';
import { usePermissions } from '@/hooks/usePermissions';
import { CrmFieldValueInput } from '@/lib/crm/components/fields/CrmFieldValueInput';
import { usePropertySchema } from '@/lib/crm/hooks/usePropertySchema';
import { saveNameRename } from '@/lib/crm/inline-edit/saveNameRename';
import { useInlineEdit } from '@/lib/crm/inline-edit/useInlineEdit';
import { NAME_DESCRIPTOR } from '@/lib/crm/inline-edit/useInlineNameRename';
import type { EntityTypeConfig } from '@/lib/crm/record/registry/entityTypeRegistry';
import { composePersonName } from '@/lib/crm/utils/getDisplayName';
import {
  hasPropertyEditor,
  isPropertyEditable,
} from '@/lib/crm/utils/propertyEditability';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';
import { cx } from '@/utils/cx';

import {
  TITLE_EDITOR_FIELD_OVERRIDES,
  TITLE_TEXT_CLASSES,
  TitleAnnouncementRegion,
  TitleReadModeContent,
  useTitleEditorAffordance,
} from './titleEditorChrome';

interface ContactNameDraft {
  first: string;
  last: string;
}

export interface RecordTitleContactEditorProps {
  object: CrmObject;
  /** Resolved entity config. Only `readOnly` is read by this component. */
  config: Pick<EntityTypeConfig, 'readOnly'>;
  /** Current display name rendered in the title. */
  displayName: string;
}

export function RecordTitleContactEditor({
  object,
  config,
  displayName,
}: RecordTitleContactEditorProps) {
  const { currentOrg } = useOrg();
  const { user } = useAuthInfo();
  const { hasPermission } = usePermissions();
  const orgId = currentOrg?.id;
  const userId = user?.id;
  const { schema } = usePropertySchema(object.org_id, object.entity_type_name);
  const firstSchema = schema['ahoy_first_name'];
  const lastSchema = schema['ahoy_last_name'];

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  const bothEditable =
    !!firstSchema &&
    isPropertyEditable(firstSchema) &&
    hasPropertyEditor(firstSchema.field_type_name) &&
    !!lastSchema &&
    isPropertyEditable(lastSchema) &&
    hasPropertyEditor(lastSchema.field_type_name);

  const canRename =
    config.readOnly !== true &&
    bothEditable &&
    hasPermission(object.entity_type_name, 'ahoy_property_updated');

  const first0 =
    getPropertyValue<string>(object.properties?.ahoy_first_name) ?? '';
  const last0 =
    getPropertyValue<string>(object.properties?.ahoy_last_name) ?? '';
  // CRITICAL: keyed on the two primitive strings, not recomputed as a fresh
  // object literal every render. `useInlineEdit`'s stale-draft resync effect
  // depends on `initialValue` BY REFERENCE (useInlineEdit.ts:135-143) — a new
  // literal every render would retrigger it, calling setDraftState, which
  // re-renders, which produces a new literal, forever. The single-name editor
  // is safe without this because its initialValue is a primitive string.
  const initialValue = useMemo<ContactNameDraft>(
    () => ({ first: first0, last: last0 }),
    [first0, last0]
  );

  // Deliberately NOT stabilizing `object` out of this dep list — see the
  // matching note on `useInlineNameRename`'s `onSaveRename`: the live
  // `getObjectSeq` read inside `saveNameRename` makes a frozen `object` safe
  // to capture, and `useInlineEdit` freezes `onSave` for the duration of an
  // edit. `orgId`/`userId` come from the top-level `useOrg`/`useAuthInfo`
  // calls above — never call those hooks inside this callback.
  const onSave = useCallback(
    async (value: unknown) => {
      const { first, last } = value as ContactNameDraft;
      const f = first.trim();
      const l = last.trim();
      if (!f && !l) {
        throw new Error('Name cannot be empty.');
      }
      if (!orgId || userId == null) {
        throw new Error('Could not save. Try again.');
      }
      const composed = composePersonName(f, l);
      await saveNameRename({
        object,
        orgId,
        userId,
        wireProperties: { ahoy_first_name: f, ahoy_last_name: l },
        optimisticProperties: {
          ahoy_first_name: f,
          ahoy_last_name: l,
          ahoy_display_name: composed,
        },
      });
    },
    [object, orgId, userId]
  );

  const {
    isEditing,
    status,
    draft,
    errorMessage,
    announcement,
    startEdit,
    cancelEdit,
    setDraft,
    handleKeyDown,
    handleBlur,
  } = useInlineEdit({
    descriptor: NAME_DESCRIPTOR,
    initialValue,
    onSave,
    containerRef,
    triggerRef,
  });

  const draftValue = draft as ContactNameDraft;

  // The seam space exists only between two present names — read mode composes
  // the title with `composePersonName`, which joins with a single space ONLY
  // when both parts are non-empty (a first-only or last-only contact renders
  // with no separator). Rendering the space unconditionally shifted a
  // last-only contact's last name right and gave a first-only contact trailing
  // width on entering edit; gating it on both trimmed parts keeps the seam
  // matching read-mode geometry and makes room as the user fills the empty
  // part.
  const showNameSeam = !!draftValue.first.trim() && !!draftValue.last.trim();

  // Focus-on-edit effect, click/keydown handlers, and trigger-ref callback
  // now live in useTitleEditorAffordance (titleEditorChrome.tsx).
  // Previously: useLayoutEffect mirroring InlineEditableFieldRow.tsx:422-440.
  const { setTriggerRef, handleTitleClick, handleTitleKeyDown } =
    useTitleEditorAffordance({
      isEditing,
      containerRef,
      triggerRef,
      startEdit,
    });

  // Both-empty handling on blur: cancel (no submit, no error-state lock-in)
  // rather than committing an empty name or delegating to the hook's default
  // blur-commits-non-equal-values behavior.
  const handleContainerBlur = useCallback(
    (e: FocusEvent<HTMLDivElement>) => {
      // Focus moving between the two name fields (or to any element still
      // inside the editor) is not a real blur: clearing one part and tabbing
      // to the other would otherwise cancel the edit before the second field
      // could be focused. Only act once focus leaves the container entirely.
      if (e.currentTarget.contains(e.relatedTarget as Node)) {
        return;
      }
      const bothEmpty =
        draftValue.first.trim().length === 0 &&
        draftValue.last.trim().length === 0;
      if (bothEmpty) {
        cancelEdit();
        return;
      }
      handleBlur(e);
    },
    [draftValue, cancelEdit, handleBlur]
  );

  // Both-empty handling on Enter: cancel instead of committing/erroring.
  // Otherwise delegate to the hook (Escape→cancel, Enter→commit).
  const handleInputKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        const bothEmpty =
          draftValue.first.trim().length === 0 &&
          draftValue.last.trim().length === 0;
        if (bothEmpty) {
          e.preventDefault();
          cancelEdit();
          return;
        }
      }
      handleKeyDown(e);
    },
    [draftValue, cancelEdit, handleKeyDown]
  );

  const handleFirstNameChange = useCallback(
    (value: unknown) => {
      setDraft({ ...draftValue, first: value as string });
    },
    [draftValue, setDraft]
  );

  const handleLastNameChange = useCallback(
    (value: unknown) => {
      setDraft({ ...draftValue, last: value as string });
    },
    [draftValue, setDraft]
  );

  return (
    <div>
      {/* Live region always rendered at the top level — outside the edit/read
          branch — so the "Saved" announcement survives the isEditing flip that
          commits and unmounts the editor in the same batch. */}
      <TitleAnnouncementRegion announcement={announcement} />

      {isEditing && firstSchema && lastSchema ? (
        <div
          ref={containerRef}
          onBlur={handleContainerBlur}
          onKeyDown={handleInputKeyDown}
        >
          <div
            className={cx(
              TITLE_EDITOR_FIELD_OVERRIDES,
              // min-w-4 (not the single-name editor's min-w-12): the last-name
              // field renders AFTER the first, so any floor wider than the
              // first name's text pushes the last name off its read-mode spot.
              // 16px keeps an emptied field clickable while editing.
              '[&_input]:min-w-4',
              // Non-wrapping: read mode truncates the composed title to a
              // single line, so the edit-mode fields must never wrap onto a
              // second line — that would grow the header taller than read
              // mode. The min-w-0 field wrappers shrink instead when the
              // composed name is wider than the header.
              'flex flex-nowrap items-center'
            )}
          >
            {/* Content-sized wrappers (not flex-1): the base Input's Group
                wrapper centers its content, so a wrapper narrower than the
                content-sized input would shift the glyphs off the read-mode
                position. Sized to the input, first-name glyphs stay exactly
                where the composed name's first glyph sat. */}
            <div className='min-w-0'>
              <CrmFieldValueInput
                kind='property'
                schema={firstSchema}
                value={draftValue.first}
                onChange={handleFirstNameChange}
                disabled={status === 'saving'}
                size='sm'
                textDisplay='card'
              />
            </div>
            {/* Literal space in the title font, NOT a flex gap: the read mode
                renders "First Last" as one text run, so the edit-mode seam
                must be exactly one space glyph wide (~0.22em) or the last
                name jumps sideways on entering edit. A fixed gap (gap-2 =
                8px vs the ~4.4px space glyph) was visibly off.

                Visibility is toggled via `hidden` (display:none), NOT
                conditionally rendered. The three flex children are unkeyed, so
                a `{showNameSeam && <span>}` that inserts/removes this fiber
                shifts React's sibling reconciliation: on the false→true flip
                (typing the first character into an empty last name) the new
                <span> aligns against the old last-name <div> by position,
                mismatches type, and the last-name <div> — and the <input>
                inside it — remounts, dropping focus and the caret after one
                character. Keeping the span mounted holds the fiber sequence
                [first, seam, last] stable so the last-name input updates in
                place. `hidden` reproduces read-mode geometry exactly: no seam
                width for a partial name (only first OR only last), matching
                read mode, which omits the separator when either part is
                empty. */}
            <span
              aria-hidden='true'
              hidden={!showNameSeam}
              className={cx(TITLE_TEXT_CLASSES, 'whitespace-pre')}
            >
              {' '}
            </span>
            <div className='min-w-0'>
              <CrmFieldValueInput
                kind='property'
                schema={lastSchema}
                value={draftValue.last}
                onChange={handleLastNameChange}
                disabled={status === 'saving'}
                size='sm'
                textDisplay='card'
              />
            </div>
          </div>
          {errorMessage && (
            <p
              role='alert'
              className='mt-1 text-xs text-[var(--color-text-error-primary)]'
            >
              {errorMessage}
            </p>
          )}
        </div>
      ) : (
        <TitleReadModeContent
          canRename={canRename}
          displayName={displayName}
          setTriggerRef={setTriggerRef}
          handleTitleClick={handleTitleClick}
          handleTitleKeyDown={handleTitleKeyDown}
        />
      )}
    </div>
  );
}
